// Frontend subscription plans configuration
// This should match the backend configuration
import { getEnvironment } from './apiKeys.js';

// Environment-specific price IDs
const PRICE_IDS = {
  staging: {
    foundation: 'price_1SABFrGg3zhMEMp9GZtxI1gE', // Test Foundation price ID
    blueprint: 'price_1SABGXGg3zhMEMp9NV2th5Dc' // Test Blueprint price ID
  },
  production: {
    foundation: 'price_1SUYYwGshSjnQ0dvzeqdoEAy', // Live Foundation price ID
    blueprint: 'price_1SUYaIGshSjnQ0dvO0voWnea' // Live Blueprint price ID
  }
};

/**
 * Get price ID for a plan based on current environment
 * This function is safe to call multiple times and will return consistent results
 * once the environment is determined and cached.
 */
const getPriceId = (planKey) => {
  const environment = getEnvironment();
  return PRICE_IDS[environment]?.[planKey] || PRICE_IDS.staging[planKey];
};

/**
 * Compute price IDs once when module loads (or when environment becomes available)
 * This ensures stable values that don't change on each access
 */
const computePriceIds = () => {
  const environment = getEnvironment();
  return {
    foundation: PRICE_IDS[environment]?.foundation || PRICE_IDS.staging.foundation,
    blueprint: PRICE_IDS[environment]?.blueprint || PRICE_IDS.staging.blueprint
  };
};

// Compute price IDs once at module initialization
const computedPriceIds = computePriceIds();

const SUBSCRIPTION_PLANS = [
  {
    key: 'foundation',
    name: 'Foundation',
    price: 49,
    credits: 25,
    priceId: computedPriceIds.foundation,
    description: 'Getting Started',
    features: [
      '25 AI CMA Credits per month',
      'Unlimited Search',
      'Unlimited Map Search',
      'Unlimited MLS GPT',
      'Virtual Staging',
      'Video Montage from your Photos',
      'Access to all features',
      'Nationwide MLS Access',
      'Email Support'
    ]
  },
  {
    key: 'blueprint',
    name: 'Blueprint',
    price: 99,
    credits: 50,
    priceId: computedPriceIds.blueprint,
    description: 'Active agents',
    features: [
      '50 AI CMA Credits per month',
      'Unlimited Search',
      'Unlimited Map Search',
      'Unlimited MLS GPT',
      'Virtual Staging',
      'Video Montage from your Photos',
      'Access to all features',
      'Nationwide MLS Access',
      'Email Support'
    ]
  }
];

/**
 * Get all subscription plans
 */
export function getSubscriptionPlans() {
  return SUBSCRIPTION_PLANS;
}

/**
 * Get plan by key
 */
export function getPlanByKey(key) {
  return SUBSCRIPTION_PLANS.find(plan => plan.key === key);
}

/**
 * Get plan by price ID
 */
export function getPlanByPriceId(priceId) {
  return SUBSCRIPTION_PLANS.find(plan => plan.priceId === priceId);
}

/**
 * Get price ID by plan key
 */
export function getPriceIdByKey(key) {
  const plan = getPlanByKey(key);
  return plan ? plan.priceId : null;
}

/**
 * Get product price IDs mapping (for backward compatibility)
 */
export function getProductPriceIds() {
  const priceIds = {};
  SUBSCRIPTION_PLANS.forEach(plan => {
    priceIds[plan.key] = plan.priceId;
  });
  return priceIds;
}
